import { useMemo, useState } from 'react';
import { ArrowLeft, CalendarDays, Check, Filter, RefreshCw, Search, X, XCircle } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import Toast from '../../components/Toast/Toast';
import ApprovalStatusBadge from '../../components/common/ApprovalStatusBadge';
import { useTheme } from '../../context/ThemeContext';
import { defaultSkillApprovalFilters, useSkillApprovals } from '../../hooks/useSkillApprovals';
import type { SkillApprovalFiltersState, SkillApprovalRow } from '../../types/admin';
import SkillApprovalsTable from './SkillApprovalsTable';
import { GetUSIDateTime } from '@/utils/useUtil';

const pageSizeOptions = [10, 20, 50, 100];

const normalizeRejectionReason = (value: string) => {
  const fourLines = value.split('\n').slice(0, 4).join('\n');
  const words = Array.from(fourLines.matchAll(/\S+/g));

  if (words.length <= 200) return fourLines;

  const lastAllowedWord = words[199];
  return fourLines.slice(0, (lastAllowedWord.index ?? 0) + lastAllowedWord[0].length);
};

const getCharacterLimitError = (value?: string | null) =>
  (value?.match(/\S/g) ?? []).length > 325 ? 'You have exceeded the character limit.' : '';

function SkillDetailsPanel({ row, onClose, onApprove, onReject }: { row: SkillApprovalRow; onClose: () => void; onApprove: () => void; onReject: () => void; }) {
  const isPending = String(row.approvalStatus || '').trim().toLowerCase() === 'pending';
  const isApproved = String(row.approvalStatus || '').trim().toLowerCase() === 'approved';
  const isRejected = String(row.approvalStatus || '').trim().toLowerCase() === 'rejected';
  const submitted = GetUSIDateTime(row.submittedDate);
  const approved = GetUSIDateTime(row.approvedDate);

  return (
    <div className="fixed inset-0 z-[80] flex justify-end bg-black/40" role="dialog" aria-modal="true" aria-label="Skill approval details" onMouseDown={(event) => { if (event.target === event.currentTarget) onClose(); }}>
      <section className="flex h-full w-full flex-col bg-[#f8fafc] shadow-2xl sm:w-[min(68vw,calc(100vw-2rem))] sm:max-w-[1100px]">
        <header className="flex items-center gap-3 border-b border-gray-200 bg-white px-5 py-4 sm:px-8">
          <span className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full bg-brand-primary text-sm font-bold text-white">{row.employeeName.split(/\s+/).filter(Boolean).slice(0, 2).map((name) => name[0]).join('').toUpperCase()}</span>
          <div className="min-w-0"><div className="flex flex-wrap items-center gap-2"><h2 className="truncate text-lg font-bold text-gray-950">{row.employeeName}</h2><ApprovalStatusBadge status={row.approvalStatus} /></div><p className="truncate text-sm text-gray-500">{row.email || 'Email not provided'}</p></div>
          <div className="ml-auto hidden text-right sm:block"><p className="text-xs text-gray-500">Submitted on</p><p className="mt-1 flex items-center gap-1.5 text-sm font-semibold text-gray-800"><CalendarDays className="h-4 w-4 text-gray-400" />{submitted.date}</p></div>
          <button type="button" onClick={onClose} className="rounded-full p-2 text-gray-500 hover:bg-gray-100" aria-label="Close skill details"><X className="h-5 w-5" /></button>
        </header>
        <div className="flex-1 space-y-6 overflow-y-auto p-5 sm:p-8">
          <div className="grid grid-cols-1 divide-y overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm sm:grid-cols-3 sm:divide-x sm:divide-y-0">
            <div className="p-4"><p className="text-xs font-medium text-gray-500">Status</p><div className="mt-2"><ApprovalStatusBadge status={row.approvalStatus} /></div></div>
            <div className="p-4"><p className="text-xs font-medium text-gray-500">Submitted On</p><p className="mt-1 text-sm font-semibold text-gray-900">{submitted.date}</p><p className="text-xs text-gray-500">{submitted.time}</p></div>
            <div className="p-4"><p className="text-xs font-medium text-gray-500">Submitted By</p><p className="mt-1 text-sm font-semibold text-gray-900">{row.employeeName}</p><p className="text-xs text-gray-500">Employee</p></div>
          </div>
          <div>
            <h3 className="mb-3 flex items-center gap-2 text-base font-bold text-gray-950"><Check className="h-5 w-5 text-brand-primary" />Skill</h3>
            <div className="grid grid-cols-1 gap-5 rounded-xl border border-gray-200 bg-white p-5 shadow-sm md:grid-cols-2">
              <div><p className="text-xs font-semibold uppercase tracking-wide text-gray-500">Skill Name</p><p className="mt-2 text-lg font-semibold text-gray-950">{row.skillName}</p></div>
              <div><p className="text-xs font-semibold uppercase tracking-wide text-gray-500">Experience</p><p className="mt-2 text-lg font-semibold text-gray-950">{row.experience} year{row.experience === 1 ? '' : 's'}</p></div>
            </div>
          </div>
          {!isPending && <div><h3 className="mb-3 text-base font-bold text-gray-950">Approval Information</h3><div className="grid grid-cols-1 gap-4 rounded-xl border border-gray-200 bg-white p-5 shadow-sm sm:grid-cols-2 lg:grid-cols-3"><div><p className="text-xs font-medium text-gray-500">{isApproved ? 'Approved By' : 'Rejected By'}</p><p className="mt-1 text-sm font-semibold text-gray-900">{row.approvedByName || row.approvedBy || 'Not provided'}</p></div><div><p className="text-xs font-medium text-gray-500">{isApproved ? 'Approved On' : 'Rejected On'}</p><p className="mt-1 text-sm font-semibold text-gray-900">{approved.date}</p><p className="text-xs text-gray-500">{approved.time}</p></div>{isRejected && <div><p className="text-xs font-medium text-gray-500">Rejection Reason</p><p className="mt-1 text-sm text-gray-900">{row.rejectionReason || 'Not provided'}</p></div>}</div></div>}
        </div>
        <footer className="flex flex-col gap-2 border-t border-gray-200 bg-white p-4 sm:flex-row sm:items-center sm:justify-between sm:px-8">
          <button type="button" onClick={onClose} className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-50">Close</button>
          {isPending && <div className="grid grid-cols-2 gap-2"><button type="button" onClick={onReject} className="inline-flex items-center justify-center rounded-lg border border-red-300 px-4 py-2 text-sm font-semibold text-red-600 hover:bg-red-50"><XCircle className="mr-2 h-4 w-4" />Reject</button><button type="button" onClick={onApprove} className="inline-flex items-center justify-center rounded-lg bg-brand-primary px-4 py-2 text-sm font-semibold text-white"><Check className="mr-2 h-4 w-4" />Approve</button></div>}
        </footer>
      </section>
    </div>
  );
}

function ApproveModal({ row, loading, onCancel, onConfirm }: { row: SkillApprovalRow; loading: boolean; onCancel: () => void; onConfirm: () => void; }) {
  return (
    <div className="fixed inset-0 z-[90] flex items-center justify-center bg-black/60 px-4" role="dialog" aria-modal="true">
      <div className="w-full max-w-sm rounded-lg bg-white p-6 shadow-xl">
        <h2 className="mb-3 text-lg font-semibold text-gray-900">Approve Skill</h2>
        <p className="mb-6 text-sm text-gray-700">
          Approve <span className="font-semibold">{row.skillName}</span> ({row.experience} yrs) for <span className="font-semibold">{row.employeeName}</span>?
        </p>
        <div className="grid grid-cols-2 gap-3">
          <button type="button" onClick={onCancel} disabled={loading} className="h-11 rounded-lg bg-gray-100 text-sm font-semibold text-gray-800">
            Cancel
          </button>
          <button type="button" onClick={onConfirm} disabled={loading} className="h-11 rounded-lg bg-brand-primary text-sm font-semibold text-white disabled:bg-gray-300">
            {loading ? 'Approving...' : 'Approve'}
          </button>
        </div>
      </div>
    </div>
  );
}

function RejectModal({
  row,
  loading,
  reason,
  error,
  onReasonChange,
  onCancel,
  onConfirm,
}: {
  row: SkillApprovalRow;
  loading: boolean;
  reason: string;
  error: string;
  onReasonChange: (value: string) => void;
  onCancel: () => void;
  onConfirm: () => void;
}) {
  const { theme } = useTheme();
  const trimmedReason = reason.trim();
  const characterLimitError = getCharacterLimitError(reason);
  const reasonValidationError = !trimmedReason
    ? ''
    : characterLimitError
      ? characterLimitError
      : trimmedReason.length < 5
        ? 'Rejection reason must be at least 5 characters.'
        : '';
  const canReject = trimmedReason.length >= 5 && !characterLimitError;

  return (
    <div className="fixed inset-0 z-[90] flex items-center justify-center bg-black/60 px-4" role="dialog" aria-modal="true">
      <div className="w-full max-w-sm rounded-lg bg-white p-6 shadow-xl">
        <h2 className="mb-1 text-lg font-semibold text-gray-900">Reject Skill</h2>
        <p className="mb-4 text-sm text-gray-500">
          {row.skillName} — {row.employeeName}
        </p>
        <label className="mb-2 block text-sm font-medium text-gray-700">Rejection Reason</label>
        <textarea
          value={reason}
          onChange={(event) => onReasonChange(normalizeRejectionReason(event.target.value))}
          rows={4}
          aria-invalid={Boolean(error || reasonValidationError)}
          className={`w-full resize-none rounded-lg border px-3 py-2 text-sm outline-none focus:border-brand-primary ${error || reasonValidationError ? 'border-red-500' : 'border-gray-300'}`}
          placeholder="Explain why this skill claim is being rejected"
          style={{ backgroundColor: theme.surfaceHover, borderColor: error || reasonValidationError ? undefined : theme.border, color: theme.text }}
        />
        {(error || reasonValidationError) && <p className="mt-2 text-xs text-red-600">{error || reasonValidationError}</p>}
        <div className="mt-5 grid grid-cols-2 gap-3">
          <button type="button" onClick={onCancel} disabled={loading} className="h-11 rounded-lg bg-gray-100 text-sm font-semibold text-gray-800">
            Cancel
          </button>
          <button type="button" onClick={onConfirm} disabled={loading || !canReject} className="h-11 rounded-lg bg-red-600 text-sm font-semibold text-white disabled:bg-gray-300">
            {loading ? 'Rejecting...' : 'Reject'}
          </button>
        </div>
      </div>
    </div>
  );
}

function FiltersPanel({
  open,
  filters,
  onChange,
  onApply,
  onReset,
  onClose,
}: {
  open: boolean;
  filters: SkillApprovalFiltersState;
  onChange: (filters: SkillApprovalFiltersState) => void;
  onApply: () => void;
  onReset: () => void;
  onClose: () => void;
}) {
  const { theme } = useTheme();
  if (!open) return null;

  const controlStyle = { backgroundColor: theme.surfaceHover, borderColor: theme.border, color: theme.text };

  return (
    <div className="fixed inset-0 z-[85] flex justify-end bg-black/40" role="dialog" aria-modal="true" onMouseDown={(event) => { if (event.target === event.currentTarget) onClose(); }}>
      <div className="flex h-full w-full max-w-sm flex-col bg-white shadow-2xl">
        <div className="flex items-center justify-between border-b border-gray-200 p-4">
          <h2 className="text-base font-bold text-gray-900">Filters</h2>
          <button type="button" onClick={onClose} className="rounded-full p-2 text-gray-500 hover:bg-gray-100" aria-label="Close filters">
            <X className="h-5 w-5" />
          </button>
        </div>
        <div className="flex-1 space-y-5 overflow-y-auto p-4">
          <div>
            <label className="mb-2 block text-sm font-medium text-gray-700">Status</label>
            <select
              value={filters.approvalStatus}
              onChange={(event) => onChange({ ...filters, approvalStatus: event.target.value })}
              className="h-11 w-full rounded-lg border border-gray-300 px-3 text-sm outline-none focus:border-brand-primary"
              style={controlStyle}
            >
              <option value="">All statuses</option>
              <option value="Pending">Pending</option>
              <option value="Approved">Approved</option>
              <option value="Rejected">Rejected</option>
              <option value="Cancelled">Cancelled</option>
            </select>
          </div>
          <div>
            <label className="mb-2 block text-sm font-medium text-gray-700">Submitted From</label>
            <input
              type="date"
              value={filters.submittedFrom}
              onChange={(event) => onChange({ ...filters, submittedFrom: event.target.value })}
              className="h-11 w-full rounded-lg border border-gray-300 px-3 text-sm outline-none focus:border-brand-primary"
              style={controlStyle}
            />
          </div>
          <div>
            <label className="mb-2 block text-sm font-medium text-gray-700">Submitted To</label>
            <input
              type="date"
              value={filters.submittedTo}
              onChange={(event) => onChange({ ...filters, submittedTo: event.target.value })}
              className="h-11 w-full rounded-lg border border-gray-300 px-3 text-sm outline-none focus:border-brand-primary"
              style={controlStyle}
            />
          </div>
        </div>
        <div className="grid grid-cols-2 gap-3 border-t border-gray-200 p-4">
          <button type="button" onClick={onReset} className="h-11 rounded-lg border border-gray-300 text-sm font-semibold text-gray-700">
            Reset
          </button>
          <button type="button" onClick={onApply} className="h-11 rounded-lg bg-brand-primary text-sm font-semibold text-white">
            Apply
          </button>
        </div>
      </div>
    </div>
  );
}

function getPageNumbers(pageNumber: number, totalPages: number) {
  const start = Math.max(1, pageNumber - 2);
  const end = Math.min(totalPages, start + 4);
  return Array.from({ length: end - start + 1 }, (_, index) => start + index);
}

const getActiveFilterCount = (filters: SkillApprovalFiltersState) =>
  [filters.approvalStatus, filters.submittedFrom, filters.submittedTo].filter(Boolean).length;

export default function SkillApprovals() {
  const navigate = useNavigate();
  const { theme } = useTheme();
  const {
    rows,
    recordsFiltered,
    recordsTotal,
    pendingCount,
    pageNumber,
    pageSize,
    search,
    sortColumn,
    sortDirection,
    filters,
    loading,
    isFetching,
    actionLoadingKey,
    error,
    setSearch,
    setPageNumber,
    changePageSize,
    changeSort,
    applyFilters,
    resetFilters,
    refresh,
    approve,
    reject,
  } = useSkillApprovals();

  const [filtersOpen, setFiltersOpen] = useState(false);
  const [draftFilters, setDraftFilters] = useState<SkillApprovalFiltersState>(filters);
  const [selectedRow, setSelectedRow] = useState<SkillApprovalRow | null>(null);
  const [approveRow, setApproveRow] = useState<SkillApprovalRow | null>(null);
  const [rejectRow, setRejectRow] = useState<SkillApprovalRow | null>(null);
  const [rejectionReason, setRejectionReason] = useState('');
  const [rejectionError, setRejectionError] = useState('');
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' | 'warning' } | null>(null);

  const activeFilterCount = getActiveFilterCount(filters);
  const totalPages = Math.max(1, Math.ceil(recordsFiltered / pageSize));
  const pageStart = recordsFiltered === 0 ? 0 : (pageNumber - 1) * pageSize + 1;
  const pageEnd = Math.min(pageNumber * pageSize, recordsFiltered);
  const pageNumbers = useMemo(() => getPageNumbers(pageNumber, totalPages), [pageNumber, totalPages]);

  const handleOpenFilters = () => {
    setDraftFilters(filters);
    setFiltersOpen(true);
  };

  const handleApplyFilters = () => {
    applyFilters(draftFilters);
    setFiltersOpen(false);
  };

  const handleResetFilters = () => {
    setDraftFilters(defaultSkillApprovalFilters);
    resetFilters();
    setFiltersOpen(false);
  };

  const handleApprove = async () => {
    if (!approveRow) return;
    try {
      await approve(approveRow);
      setToast({ message: 'Skill approved successfully.', type: 'success' });
      setApproveRow(null);
      setSelectedRow(null);
    } catch (err: any) {
      setToast({ message: err?.message || 'Failed to approve skill.', type: 'error' });
    }
  };

  const handleReject = async () => {
    if (!rejectRow) return;
    const reason = rejectionReason.trim();
    if (!reason) { setRejectionError('Rejection reason is required.'); return; }
    if (reason.length < 5) { setRejectionError('Rejection reason must be at least 5 characters.'); return; }

    try {
      await reject(rejectRow, reason);
      setToast({ message: 'Skill rejected successfully.', type: 'success' });
      setRejectRow(null);
      setSelectedRow(null);
      setRejectionReason('');
      setRejectionError('');
    } catch (err: any) {
      setToast({ message: err?.message || 'Failed to reject skill.', type: 'error' });
    }
  };

  return (
    <div className="min-h-screen bg-[#f5f7fa]">
      <div className="mx-auto w-full max-w-7xl px-4 py-4">
        <div className="mb-5 rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
          <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
            <div className="flex items-center gap-3">
              <button
                type="button"
                onClick={() => navigate(-1)}
                className="flex h-11 w-11 items-center justify-center rounded-lg bg-gray-50"
                style={{ color: theme.primary }}
                aria-label="Back"
              >
                <ArrowLeft className="h-5 w-5" />
              </button>
              <div>
                <div className="flex flex-wrap items-center gap-2">
                  <h1 className="text-xl font-bold text-gray-900">Skill Approvals</h1>
                  <span className="inline-flex min-w-7 items-center justify-center rounded-full bg-red-600 px-2 py-1 text-xs font-bold text-white">
                    {pendingCount}
                  </span>
                </div>
                <p className="mt-1 text-sm text-gray-500">Review employee-submitted skills and approve or reject requests.</p>
              </div>
            </div>

            <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
              <div className="relative">
                <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
                <input
                  value={search}
                  onChange={(event) => setSearch(event.target.value)}
                  placeholder="Search employee or skill..."
                  className="h-11 w-full rounded-lg border border-gray-300 pl-9 pr-3 text-sm outline-none focus:border-brand-primary sm:w-64"
                  style={{ backgroundColor: theme.surface, borderColor: theme.border, color: theme.text }}
                />
              </div>
              <button
                type="button"
                onClick={() => refresh().catch(() => {})}
                disabled={isFetching}
                className="inline-flex h-11 items-center justify-center gap-2 rounded-lg border border-gray-200 px-4 text-sm font-semibold text-gray-700 hover:bg-gray-50 disabled:opacity-60"
              >
                <RefreshCw className={`h-4 w-4 ${isFetching ? 'animate-spin' : ''}`} />
                Refresh
              </button>
              <button
                type="button"
                onClick={handleOpenFilters}
                className="inline-flex h-11 items-center justify-center gap-2 rounded-lg bg-brand-primary px-4 text-sm font-semibold text-white"
              >
                <Filter className="h-4 w-4" />
                Filters
                {activeFilterCount > 0 && (
                  <span className="rounded-full bg-white px-2 py-0.5 text-xs font-bold text-brand-primary">
                    {activeFilterCount}
                  </span>
                )}
              </button>
            </div>
          </div>
        </div>

        {error && (
          <div className="mb-4 rounded-lg border border-red-200 bg-red-50 p-4">
            <p className="text-sm text-red-700">{error}</p>
            <button type="button" onClick={() => refresh().catch(() => {})} className="mt-3 text-sm font-semibold text-red-700">
              Retry
            </button>
          </div>
        )}

        {rows.length === 0 && !loading ? (!error && (
          <div className="rounded-lg border border-gray-200 bg-white p-8 text-center shadow-sm">
            <p className="text-sm text-gray-500">No skill requests found.</p>
          </div>
        )) : (
          <div className={`transition ${isFetching && !loading ? 'opacity-70' : 'opacity-100'}`}>
            <SkillApprovalsTable
              rows={rows}
              loading={loading}
              actionLoadingKey={actionLoadingKey}
              sortColumn={sortColumn}
              sortDirection={sortDirection}
              onSort={changeSort}
              onView={setSelectedRow}
              onApprove={setApproveRow}
              onReject={(row) => { setRejectRow(row); setRejectionReason(''); setRejectionError(''); }}
            />
          </div>
        )}

        <div className="mt-4 flex flex-col gap-3 rounded-lg border border-gray-200 bg-white px-4 py-3 shadow-sm lg:flex-row lg:items-center lg:justify-between">
          <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
            <p className="mb-0 text-sm text-gray-600">
              Showing {pageStart}-{pageEnd} of {recordsFiltered} requests
              {recordsTotal !== recordsFiltered ? ` (${recordsTotal} total)` : ''}
            </p>
            <select
              value={pageSize}
              onChange={(event) => changePageSize(Number(event.target.value))}
              className="h-10 rounded-lg border border-gray-200 pl-3 pr-7 text-sm outline-none"
              style={{ backgroundColor: theme.surface, borderColor: theme.border, color: theme.text }}
            >
              {pageSizeOptions.map((option) => (
                <option key={option} value={option}>{option}</option>
              ))}
            </select>
          </div>

          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              disabled={pageNumber <= 1 || isFetching}
              onClick={() => setPageNumber(Math.max(pageNumber - 1, 1))}
              className="h-10 rounded-lg border border-gray-200 px-4 text-sm font-semibold text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
            >
              Previous
            </button>
            {pageNumbers.map((page) => (
              <button
                key={page}
                type="button"
                disabled={isFetching}
                onClick={() => setPageNumber(page)}
                className={`h-10 min-w-10 rounded-lg px-3 text-sm font-semibold ${
                  page === pageNumber
                    ? 'bg-brand-primary text-white'
                    : 'border border-gray-200 text-gray-700 hover:bg-gray-50'
                }`}
              >
                {page}
              </button>
            ))}
            <button
              type="button"
              disabled={pageNumber >= totalPages || isFetching}
              onClick={() => setPageNumber(Math.min(pageNumber + 1, totalPages))}
              className="h-10 rounded-lg border border-gray-200 px-4 text-sm font-semibold text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
            >
              Next
            </button>
          </div>
        </div>
      </div>

      <FiltersPanel
        open={filtersOpen}
        filters={draftFilters}
        onChange={setDraftFilters}
        onApply={handleApplyFilters}
        onReset={handleResetFilters}
        onClose={() => setFiltersOpen(false)}
      />

      {selectedRow && <SkillDetailsPanel row={selectedRow} onClose={() => setSelectedRow(null)} onApprove={() => setApproveRow(selectedRow)} onReject={() => { setRejectRow(selectedRow); setRejectionReason(''); setRejectionError(''); }} />}

      {approveRow && (
        <ApproveModal
          row={approveRow}
          loading={actionLoadingKey === String(approveRow.userSkillId)}
          onCancel={() => setApproveRow(null)}
          onConfirm={handleApprove}
        />
      )}

      {rejectRow && (
        <RejectModal
          row={rejectRow}
          loading={actionLoadingKey === String(rejectRow.userSkillId)}
          reason={rejectionReason}
          error={rejectionError}
          onReasonChange={(value) => {
            setRejectionReason(value);
            if (value.trim().length >= 5) setRejectionError('');
          }}
          onCancel={() => setRejectRow(null)}
          onConfirm={handleReject}
        />
      )}

      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}
    </div>
  );
}
